import { Button, ButtonLink, ButtonProps, Card, Text } from '@pm/ui';
import { ReactNode } from 'react';
import { To } from 'react-router';

type SharedActionProps = {
  text: string;
  intent?: Extract<ButtonProps['intent'], 'primary' | 'secondary' | 'ghost'>;
};

type ActionButton = SharedActionProps & {
  to?: never;
  disabled?: boolean;
  // FIXME: If there's an action, `onClick` should be required
  onClick?: () => void;
  trailingIcon?: ReactNode;
};

type ActionLink = SharedActionProps & {
  to: To;
  disabled?: never;
  onClick?: never;
  trailingIcon?: ReactNode;
};

export type Action = ActionButton | ActionLink;

type WorkflowStepCardProps = {
  title: string;
  description?: string;
  action: Action;
  secondaryAction?: Action;
};

export const WorkflowStepCard = ({
  title,
  description,
  action,
  secondaryAction,
}: WorkflowStepCardProps) => {
  const { text: actionText, intent: primaryIntent, ...actionProps } = action;
  let secondaryActionEl;

  const primaryActionEl = actionProps.to ? (
    <ButtonLink
      {...actionProps}
      width="full"
      intent={primaryIntent}
      trailingIcon={actionProps?.trailingIcon}
    >
      {actionText}
    </ButtonLink>
  ) : (
    <Button
      {...actionProps}
      width="full"
      intent={primaryIntent}
      trailingIcon={actionProps?.trailingIcon}
    >
      {actionText}
    </Button>
  );

  if (secondaryAction) {
    const {
      text: secondaryActionText,
      intent: secondaryIntent,
      ...secondaryActionProps
    } = secondaryAction;

    secondaryActionEl =
      secondaryAction &&
      (secondaryActionProps.to ? (
        <ButtonLink
          {...secondaryActionProps}
          width="full"
          intent={secondaryIntent}
        >
          {secondaryActionText}
        </ButtonLink>
      ) : (
        <Button {...secondaryActionProps} width="full" intent={secondaryIntent}>
          {secondaryActionText}
        </Button>
      ));
  }

  return (
    <Card size="large">
      <div className="flex-col p-s space-y-m">
        <div className="space-y-s">
          {title !== '' && (
            <Text as="h2" size="headingXS">
              {title}
            </Text>
          )}
          <Text
            as="p"
            size="bodyL"
            color="contentDefault"
            className="whitespace-pre-line"
          >
            {description}
          </Text>
        </div>

        <div className="flex gap-l">
          {primaryActionEl}
          {secondaryActionEl}
        </div>
      </div>
    </Card>
  );
};
